import classNames from 'classnames';
import { X } from 'lucide-react';
import React, { FC, memo, ReactNode, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useScrollLock } from 'usehooks-ts';

type PlacementDialog =
  | 'CENTER'
  | 'TOP_CENTER'
  | 'TOP_LEFT'
  | 'TOP-RIGHT'
  | 'BOTTOM_LEFT'
  | 'BOTTOM_RIGHT';

export type SizeDialog =
  | 'sm'
  | 'md'
  | 'lg'
  | 'xl'
  | 'xxl'
  | '3xl'
  | '4xl'
  | 'full';

interface IDialogProps {
  visible?: boolean;
  title?: ReactNode;
  children?: ReactNode;
  footer?: ReactNode;
  placement?: PlacementDialog;
  size?: SizeDialog;
  hideOnClickOutside?: boolean;
  onClose?: () => void;
  customSize?: string;
}

const Dialog: FC<IDialogProps> = ({
  visible = false,
  size = 'md',
  title,
  children,
  onClose,
  customSize,
  hideOnClickOutside = false,
}) => {
  const getSizeDialog = (): string => {
    const sizeObj: Record<SizeDialog, string> = {
      sm: 'max-w-sm',
      md: 'max-w-md',
      lg: 'max-w-2xl',
      xl: 'max-w-3xl',
      xxl: 'max-w-4xl',
      '3xl': 'max-w-6xl',
      '4xl': 'max-w-7xl',
      full: 'max-w-full',
    };
    return sizeObj[size];
  };

  useScrollLock({
    autoLock: visible,
    lockTarget: 'body',
  });

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && visible && onClose) {
        onClose();
      }
    };

    if (visible) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [visible, onClose]);

  const handleOverlayClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (event.target === event.currentTarget && hideOnClickOutside && onClose) {
      console.log('Overlay clicked, hideOnClickOutside:', hideOnClickOutside);
      onClose();
    }
  };

  const handleDialogClick = (event: React.MouseEvent<HTMLDivElement>) => {
    event.stopPropagation(); // Prevent clicks inside dialog from reaching overlay
  };
  return ReactDOM.createPortal(
    visible ? (
      <div
        className="fixed inset-0 z-[1000] overflow-y-auto bg-black/50 lg:p-20"
        onClick={handleOverlayClick}
      >
        <div
          className={classNames(
            // items-start → nội dung cao hơn màn hình vẫn cuộn hết (không bị flex-center cắt mất đầu,
            // vd popup xem bài trên mobile: ảnh h-[85vh] + bình luận). Giữ canh trên như default cũ (TOP_CENTER).
            ['mx-auto w-full flex min-h-full justify-center py-8 items-start', getSizeDialog()]
          )}
          onClick={(e) => e.stopPropagation()}
        >
          <div
            className={classNames(
              // max-w-[95vw] + overflow-hidden: modal luôn nằm gọn trong màn (không tràn/cắt trái trên mobile)
              `dialog-content bg-white dark:bg-zinc-800 rounded-2xl text-zinc-900 dark:text-zinc-50 max-w-[95vw] overflow-hidden ${customSize}`
            )}
          >
            {title && (
              <div className="flex items-center justify-between gap-3 px-6 py-4 border-b border-[#EEF0F4] dark:border-slate-700">
                <h4 className="text-[#18191E] dark:text-zinc-50 text-xl md:text-[22px] leading-tight font-semibold">
                  {title}
                </h4>

                {onClose ? (
                  <button
                    type="button"
                    aria-label="Đóng"
                    className="grid place-items-center w-9 h-9 shrink-0 rounded-full text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-700 active:scale-95 transition"
                    onClick={() => {
                      onClose();
                    }}
                  >
                    <X size={20} strokeWidth={2} />
                  </button>
                ) : null}
              </div>
            )}
            <div>{children}</div>
          </div>
        </div>
      </div>
    ) : null,
    document.getElementById('dialog-root') || ({} as HTMLElement)
  );
};

export default memo(Dialog);
